import { useRequestURL } from '#imports'

import { normalizeLocaleCodeValue } from '@packages/i18n'
import axios, { type AxiosRequestConfig } from 'axios'

export type Period = '1m' | '5m' | '15m' | '30m' | '1h' | '4h' | '1d' | '1w' | '1M'

export interface KLinePoint {
  time: number
  open: number
  high: number
  low: number
  close: number
  volume?: number
}

export interface BrokerSymbol {
  symbol: string
  base_currency?: string
  settlement_currency?: string
  base_currency_name?: string
  settlement_currency_name?: string
  price_precision?: number
  price_tick?: number
  lot_size?: number
  show_quote?: number
  show_status?: number
  unordered?: number
  icon?: string
  symbol_code?: string
  category?: string
  category_code?: string
  group?: string
  symbol_category?: string
  symbol_category_code?: string
  symbol_type?: string
  type?: string
  [key: string]: unknown
}

export interface SymbolCategory {
  code: string
  zh_name?: string
  en_name?: string
  name?: string
  sort?: number
  unordered?: number
}

export type CategoryTranslationMap = Record<string, string>

// 当前文件只维护 WikiTrade 行情所需的 HTTP 接口、鉴权缓存和返回结构归一化。
// WebSocket、图表 datafeed 和页面展示工具放在 pages/market 下，避免接口层承担页面状态。
interface StoredAccessToken {
  accessToken: string
  expiresAt: number
}

interface CachedResourceState<T> {
  promise: Promise<T> | null
  expiresAt: number
}

interface CachedResourceOptions<T> {
  state: CachedResourceState<T>
  storageKey: string
  ttlMs: number
  isUsableCache: (value: unknown) => value is T
  request: () => Promise<T>
}

interface WikiTradeRequestOptions {
  languageCode?: string
}

// 历史 K 线接口的原始字段以字符串为主，解析后统一转换成 KLinePoint。
interface KlineRow {
  timestamp: number
  open_price: string
  high_price: string
  low_price: string
  close_price: string
  volume?: string | number
  vol?: string | number
  v?: string | number
  tick_volume?: string | number
  trade_volume?: string | number
  turnover_volume?: string | number
  volume_value?: string | number
}

const HISTORY_LIMIT = 300

// WikiTrade 行情页的本地缓存 key：access_token、品种配置和分类翻译写入 localStorage，
// 用于减少重复鉴权请求，并让弹窗基础数据可以优先从缓存恢复。
const ACCESS_TOKEN_STORAGE_KEY = 'wikitrade.oauth2.accessToken.v2'
const INIT_CONFIG_STORAGE_KEY = 'wikitrade.cache.initConfig.v2'
const TRANSLATIONS_STORAGE_KEY = 'wikitrade.cache.translations.v2'

// 聚合层要求 BasicData/basicdata1 请求头携带设备指纹；本地没有指纹时使用该默认值初始化。
const BASIC_DATA_STORAGE_KEY = 'wikitrade.basicData.fingerprint.interface002'
const BASIC_DATA_FINGERPRINT = '822ca7b3261a0b2ec10c8cdabfb5a0ea'

// 聚合行情接口请求头里的业务方/券商标识，服务端用它识别当前 WikiTrade 行情来源。
const WIKI_BROKER_CODE = '1627938149'
const TOKEN_REFRESH_AHEAD_MS = 5 * 60 * 1000
const TOKEN_FALLBACK_EXPIRES_IN_MS = 20 * 60 * 1000
const INIT_CONFIG_TTL_MS = 10 * 60 * 1000
const TRANSLATIONS_TTL_MS = 30 * 60 * 1000

const GLOBAL_TRANSLATE_ROUTE = 'simulationtradetemp$golbaltranslate'
const INIT_CONFIG_ROUTE = 'simulationtradetemp$initconfig'
const SYMBOL_CATEGORY_ROUTE = 'simulationtradetemp$configsymbolcategory'
const KLINES_ROUTE = 'simulationtradetemp$klines'
const SYMBOL_CONFIG_ROUTE = 'simulationtradetemp$getsymbolconfig'

/** 品种名称/类型及多语言配置，用于图例与搜索副标题。 */
export interface WikiTradeSymbolConfig {
  symbol: string
  symbol_name?: string
  symbol_type?: number
  translations?: Record<string, string>
}

export interface HighlightedTextPart {
  text: string
  highlight: boolean
}

export type MarketTickerItem = {
  id: string
  symbol: string
  price: string
  change: string
  trend: 'up' | 'down'
  icon: string
}

export type MarketTickerResponse = {
  data?: {
    simulationtradetemp?: {
      symbolcategorymarketlist?: {
        data?: {
          list?: MarketTickerItem[]
        }
      }
    }
  }
  simulationtradetemp?: {
    symbolcategorymarketlist?: {
      data?: {
        list?: MarketTickerItem[]
      }
    }
  }
}

// 模块级 Promise 用来合并同一时间窗口内的重复请求，避免页面多个 watcher 同时打接口。
let accessTokenPromise: Promise<string> | null = null
const initConfigCaches = new Map<string, CachedResourceState<BrokerSymbol[]>>()
const translationsCaches = new Map<string, CachedResourceState<CategoryTranslationMap>>()

class WikiTradeHttpError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'WikiTradeHttpError'
    this.status = status
  }
}

// 聚合接口成功/失败字段不完全统一，这里先把 axios 响应归一成业务 payload 或错误。
const wikiTradeRequest = async (config: AxiosRequestConfig) => {
  const response = await axios.request({
    timeout: 15_000,
    ...config,
  })
  const payload = response.data

  if (payload?.error) {
    throw new Error(payload.error_description || payload.error)
  }
  if (payload?.succeed === false || payload?.success === false) {
    throw new Error(payload.message || payload.msg || '接口返回失败。')
  }

  return payload as unknown
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

const canUseStorage = () => typeof window !== 'undefined' && Boolean(window.localStorage)

const normalizeLanguageCode = (languageCode?: string) => {
  const normalized = (languageCode || '').trim().replaceAll('_', '-').toLowerCase()

  if (!normalized) return 'zh-cn'
  if (normalized === 'zh' || normalized === 'zh-cn') return 'zh-cn'
  if (normalized === 'en' || normalized === 'en-us') return 'en'

  return normalized
}

const getLanguageCacheKey = (storageKey: string, languageCode: string) => {
  return `${storageKey}:${languageCode.toLowerCase()}`
}

const getCachedResourceState = <T>(cacheMap: Map<string, CachedResourceState<T>>, cacheKey: string) => {
  const cachedState = cacheMap.get(cacheKey)

  if (cachedState) {
    return cachedState
  }

  const state: CachedResourceState<T> = {
    promise: null,
    expiresAt: 0,
  }
  cacheMap.set(cacheKey, state)
  return state
}

// 本地缓存统一处理 TTL，读取到过期数据会顺手删除，避免下次继续命中过期内容。
const readLocalCache = (key: string, options: { ttlMs?: number } = {}): unknown => {
  if (!canUseStorage()) return null

  try {
    const text = window.localStorage.getItem(key)
    if (!text) return null
    const payload: unknown = JSON.parse(text)

    if (!isRecord(payload)) {
      window.localStorage.removeItem(key)
      return null
    }

    const expiresAt =
      (typeof payload.expiresAt === 'number' ? payload.expiresAt : 0) ||
      (typeof payload.createdAt === 'number' && options.ttlMs ? payload.createdAt + options.ttlMs : 0)

    if (expiresAt && expiresAt <= Date.now()) {
      window.localStorage.removeItem(key)
      return null
    }

    return payload.value
  } catch {
    window.localStorage.removeItem(key)
    return null
  }
}

// 行情页写入 localStorage 的缓存 key 前缀，配额不足时只回收这些自己产生的缓存。
const CACHE_KEY_PREFIXES = ['wikitrade.cache.', 'wikitrade.oauth2.', 'wikitrade.websocket.']

/**
 * 回收行情页已过期的 localStorage 缓存，为新的写入腾出空间。
 * 没有 expiresAt 的条目视为长期有效，不做清理。
 */
export const pruneExpiredLocalCache = () => {
  if (!canUseStorage()) return

  Object.keys(window.localStorage).forEach((key) => {
    if (!CACHE_KEY_PREFIXES.some((prefix) => key.startsWith(prefix))) return

    try {
      const payload: unknown = JSON.parse(window.localStorage.getItem(key) || '')
      const expiresAt =
        isRecord(payload) && typeof payload.expiresAt === 'number' ? payload.expiresAt : Number.POSITIVE_INFINITY
      if (expiresAt > Date.now()) return
      window.localStorage.removeItem(key)
    } catch {
      // 无法解析的脏数据直接删除。
      window.localStorage.removeItem(key)
    }
  })
}

// 旧版本把 initconfig 按语言分别写入（key 带语言后缀），每份约 1.5MB。
// initconfig 本身与语言无关，改回单 key 后清掉这些历史条目，避免继续占用 localStorage 配额。
const removeLegacyInitConfigCaches = () => {
  if (!canUseStorage()) return

  Object.keys(window.localStorage).forEach((key) => {
    if (key.startsWith(`${INIT_CONFIG_STORAGE_KEY}:`)) {
      window.localStorage.removeItem(key)
    }
  })
}

// 写缓存时保存 createdAt 和 expiresAt，方便兼容不同版本的读取逻辑。
// localStorage 只用于减少请求，写入失败（例如配额超限）必须降级为不缓存，不能让接口调用整体失败。
const writeLocalCache = <T>(key: string, value: T, options: { ttlMs?: number } = {}) => {
  if (!canUseStorage()) return

  const now = Date.now()
  const text = JSON.stringify({
    value,
    createdAt: now,
    expiresAt: options.ttlMs ? now + options.ttlMs : undefined,
  })

  try {
    window.localStorage.setItem(key, text)
    return
  } catch {
    // 配额不足时先回收本模块的过期缓存，再重试一次。
    pruneExpiredLocalCache()
  }

  try {
    window.localStorage.setItem(key, text)
  } catch {
    // 仍然写入失败则放弃本次缓存，数据照常返回给调用方。
  }
}

const removeLocalCache = (key: string) => {
  if (!canUseStorage()) return
  window.localStorage.removeItem(key)
}

const getBasicDataHeader = () => {
  const stored = canUseStorage() ? window.localStorage.getItem(BASIC_DATA_STORAGE_KEY) : ''
  const fingerprint = stored || BASIC_DATA_FINGERPRINT

  if (!stored && canUseStorage()) {
    window.localStorage.setItem(BASIC_DATA_STORAGE_KEY, fingerprint)
  }

  return `999,0,100,0,0,${fingerprint},0`
}

// access token 可能已经带 Bearer 前缀，发请求前统一剥掉再由请求头模板拼回去。
const stripBearerPrefix = (token: string) => token.trim().replace(/^Bearer\s+/i, '')

// 对外只暴露可读错误文案，优先使用服务端返回的业务错误，再退回通用提示。
const getRequestErrorMessage = (error: unknown, fallback = '请求失败，请稍后重试。') => {
  if (!isRecord(error)) return fallback

  const response = isRecord(error.response) ? error.response : null
  const data = isRecord(response?.data) ? response.data : null
  const message = typeof error.message === 'string' ? error.message : ''

  return (
    (typeof data?.error_description === 'string' ? data.error_description : '') ||
    (typeof data?.message === 'string' ? data.message : '') ||
    (typeof data?.msg === 'string' ? data.msg : '') ||
    (typeof data?.error === 'string' ? data.error : '') ||
    message ||
    fallback
  )
}

export const getErrorMessage = (error: unknown) => getRequestErrorMessage(error)

const buildAuthHeaders = (languageCode = 'zh-CN') => {
  const basicData = getBasicDataHeader()
  const normalizedLanguageCode = normalizeLanguageCode(languageCode)

  return {
    basicdata1: basicData,
    BasicData: basicData,
    LanguageCode: normalizedLanguageCode,
  }
}

// authorize 的 code 在不同环境可能直接返回 code，也可能放在 redirect_uri 里。
// 这里统一提取，后面的 token 换取流程只处理明确的授权码。
const extractAuthCode = (payload: unknown) => {
  const root = isRecord(payload) ? payload : {}
  const data = isRecord(root.data) ? root.data : {}
  const candidates = [
    root?.code,
    data?.code,
    root?.redirect_uri,
    root?.redirectUri,
    root?.redirect_url,
    data?.redirect_uri,
    data?.redirectUri,
    data?.redirect_url,
    typeof payload === 'string' ? payload : '',
  ].filter(Boolean)

  for (const candidate of candidates) {
    const text = String(candidate)
    if (/^[\w.-]+$/.test(text) && !text.includes('://')) {
      return text
    }

    try {
      // 解析相对地址需要 base；SSR 下没有 window，改用当前请求域名（原为硬编码生产域名）。
      const baseUrl = typeof window === 'undefined' ? useRequestURL().origin : window.location.origin
      const url = new URL(text, baseUrl)
      const code = url.searchParams.get('code')
      if (code) return code
    } catch {
      const matched = /[?&]code=([^&]+)/.exec(text)
      if (matched?.[1]) return decodeURIComponent(matched[1])
    }
  }

  throw new Error('OAuth2 授权接口未返回 code。')
}

const extractAccessTokenPayload = (payload: unknown) => {
  if (typeof payload === 'string') {
    const searchParams = new URLSearchParams(payload)
    return {
      accessToken: searchParams.get('access_token') || '',
      expiresIn: Number(searchParams.get('expires_in')),
    }
  }

  const root = isRecord(payload) ? payload : {}
  const data = isRecord(root.data) ? root.data : {}

  return {
    accessToken:
      (typeof root.access_token === 'string' ? root.access_token : '') ||
      (typeof data.access_token === 'string' ? data.access_token : ''),
    expiresIn: Number(root.expires_in || data.expires_in),
  }
}

const isStoredAccessToken = (value: unknown): value is StoredAccessToken => {
  return isRecord(value) && typeof value.accessToken === 'string' && typeof value.expiresAt === 'number'
}

// access token 提前 5 分钟视为失效，给后续行情接口留出刷新缓冲。
const readStoredAccessToken = () => {
  const stored = readLocalCache(ACCESS_TOKEN_STORAGE_KEY)
  if (!isStoredAccessToken(stored)) return null
  if (stored.expiresAt <= Date.now() + TOKEN_REFRESH_AHEAD_MS) return null
  return stored.accessToken
}

const saveAccessToken = (accessToken: string, expiresIn?: number) => {
  const ttlMs =
    typeof expiresIn === 'number' && Number.isFinite(expiresIn)
      ? Math.max(60, expiresIn) * 1000
      : TOKEN_FALLBACK_EXPIRES_IN_MS

  writeLocalCache(
    ACCESS_TOKEN_STORAGE_KEY,
    {
      accessToken,
      expiresAt: Date.now() + ttlMs,
    },
    { ttlMs },
  )
}

const requestAccessToken = async (baseURL: string, options: WikiTradeRequestOptions = {}) => {
  const headers = buildAuthHeaders(options.languageCode)
  const authParams = {
    clientId: CLIENT_ID,
    clientSecret: CLIENT_SECRET,
    provisionKey: 'wiki-aggregation-layer-key',
    scope: 'email address',
    userId: 'authenticated_tester',
  }

  // 第一步先向授权接口换 code。聚合层沿用 OAuth2 形态，但这是页面侧的机器身份授权，
  // 所以 client/provision/user 这些参数是固定配置，不来自用户输入。
  const authorizePayload = await wikiTradeRequest({
    baseURL,
    url: '/oauth2/authorize',
    method: 'POST',
    headers,
    data: {
      response_type: 'code',
      scope: authParams.scope,
      client_id: authParams.clientId,
      provision_key: authParams.provisionKey,
      authenticated_userid: authParams.userId,
    },
  })
  const code = extractAuthCode(authorizePayload)

  // 第二步使用 code 换 access_token。服务端可能返回 JSON，也可能返回
  // application/x-www-form-urlencoded 文本，extractAccessTokenPayload 会统一处理。
  const tokenPayload = await wikiTradeRequest({
    baseURL,
    url: '/oauth2/token',
    method: 'POST',
    headers,
    data: {
      grant_type: 'authorization_code',
      client_id: authParams.clientId,
      client_secret: authParams.clientSecret,
      code,
    },
  })
  const { accessToken, expiresIn } = extractAccessTokenPayload(tokenPayload)

  if (!accessToken) {
    throw new Error('OAuth2 token 接口未返回 access_token。')
  }

  saveAccessToken(accessToken, expiresIn)

  return accessToken
}

// 页面初始化前先换取聚合层 access token，后续行情接口都复用这一份授权。
export const createPageAccessToken = (
  baseURL: string,
  options: WikiTradeRequestOptions & { forceRefresh?: boolean } = {},
) => {
  if (!options.forceRefresh) {
    const stored = readStoredAccessToken()
    if (stored) return Promise.resolve(stored)
  }

  if (!accessTokenPromise) {
    accessTokenPromise = requestAccessToken(baseURL, options)
      .catch((error) => {
        removeLocalCache(ACCESS_TOKEN_STORAGE_KEY)
        throw error
      })
      .finally(() => {
        accessTokenPromise = null
      })
  }

  return accessTokenPromise
}

const buildAggregateHeaders = (route: string, accessToken: string, languageCode = 'zh-CN') => {
  const basicData = getBasicDataHeader()
  const normalizedLanguageCode = normalizeLanguageCode(languageCode)

  return {
    Accept: 'application/json, text/plain, */*',
    Authorization: `Bearer ${stripBearerPrefix(accessToken)}`,
    BasicData: basicData,
    basicdata1: basicData,
    CountryCode: '702',
    LanguageCode: normalizedLanguageCode,
    PreferredLanguageCode: normalizedLanguageCode,
    Routes: route,
    TradeToken: '',
    'Web-Version': '5',
    WikiBrokerCode: WIKI_BROKER_CODE,
  }
}

// invoke-v3 的 Routes 可能随环境或服务版本变化，调用方通过 fallback route 列表提高兼容性。
const requestAggregate = async (
  baseURL: string,
  route: string,
  data: Record<string, unknown>,
  accessToken: string,
  options: WikiTradeRequestOptions = {},
) => {
  try {
    const languageCode = normalizeLanguageCode(options.languageCode)

    return await wikiTradeRequest({
      baseURL,
      url: '/forexpayweb-v1/invoke-v3/tradequote',
      method: 'POST',
      headers: buildAggregateHeaders(route, accessToken, languageCode),
      data,
    })
  } catch (error: unknown) {
    const response = isRecord(error) && isRecord(error.response) ? error.response : null
    const status = typeof response?.status === 'number' ? response.status : 0
    throw new WikiTradeHttpError(status, getRequestErrorMessage(error))
  }
}

// 如果聚合层返回 401，只刷新一次 token 并重放原请求，避免页面直接感知授权过期。
const withWikiTradeAuth = async <T>(
  baseURL: string,
  accessToken: string,
  task: (accessToken: string) => Promise<T>,
  options: WikiTradeRequestOptions & { onTokenRefreshed?: (newToken: string) => void } = {},
) => {
  try {
    return await task(accessToken)
  } catch (error) {
    if (!(error instanceof WikiTradeHttpError) || error.status !== 401) {
      throw error
    }

    const refreshedToken = await createPageAccessToken(baseURL, {
      forceRefresh: true,
      languageCode: options.languageCode,
    })
    options.onTokenRefreshed?.(refreshedToken)
    return task(refreshedToken)
  }
}

/** 拉取首页行情 ticker，统一复用 WikiTrade 聚合鉴权、请求头和 401 重试逻辑。 */
export const fetchMarketTicker = async (
  baseURL: string,
  accessToken: string,
  options: WikiTradeRequestOptions = {},
): Promise<MarketTickerItem[]> => {
  const languageCode = normalizeLanguageCode(options.languageCode)
  const payload = await withWikiTradeAuth(
    baseURL,
    accessToken,
    (token) => requestAggregate(baseURL, 'simulationtradetemp$symbolcategorymarketlist', {}, token, { languageCode }),
    { languageCode },
  )

  const response = payload as MarketTickerResponse
  return (
    response.data?.simulationtradetemp?.symbolcategorymarketlist?.data?.list ||
    response.simulationtradetemp?.symbolcategorymarketlist?.data?.list ||
    []
  )
}

const isMemoryCacheFresh = (expiresAt: number) => expiresAt > Date.now()

// 资源类接口都走同一套缓存策略：
// 1. 内存 Promise 仍在有效期内时直接复用，避免同一页面重复请求。
// 2. localStorage 有可用数据时先秒开页面，再在后台静默刷新。
// 3. 首次请求失败时清掉内存占位，下一次操作可以重新发起请求。
const refreshCachedResource = <T>(options: CachedResourceOptions<T>) => {
  options
    .request()
    .then((data) => {
      options.state.promise = Promise.resolve(data)
      options.state.expiresAt = Date.now() + options.ttlMs
      return data
    })
    .catch(() => undefined)
}

const loadCachedResource = <T>(options: CachedResourceOptions<T>) => {
  const { state, storageKey, ttlMs, isUsableCache, request } = options

  if (state.promise && isMemoryCacheFresh(state.expiresAt)) {
    return state.promise
  }

  const cached = readLocalCache(storageKey, { ttlMs })
  if (isUsableCache(cached)) {
    state.promise = Promise.resolve(cached)
    state.expiresAt = Date.now() + ttlMs
    refreshCachedResource(options)
    return state.promise
  }

  if (state.promise) return state.promise

  state.promise = request()
    .then((data) => {
      state.expiresAt = Date.now() + ttlMs
      return data
    })
    .catch((error) => {
      state.promise = null
      state.expiresAt = 0
      throw error
    })

  return state.promise
}

const findFirstList = (value: unknown, depth = 0): unknown[] | null => {
  if (depth > 8 || value === null || value === undefined) return null
  if (Array.isArray(value)) return value
  if (!isRecord(value)) return null

  if (Array.isArray(value.list)) return value.list

  for (const key of ['data', 'simulationtradetemp', 'klines', 'symbol', 'symbols']) {
    const list = findFirstList(value[key], depth + 1)
    if (list) return list
  }

  for (const child of Object.values(value)) {
    const list = findFirstList(child, depth + 1)
    if (list) return list
  }

  return null
}

// 聚合接口有时会把 list 包在 data、模块名或更深层对象里，递归查找可以隔离这些差异。
const readList = (payload: unknown) => findFirstList(payload) || []

const findFirstArrayByKey = (value: unknown, targetKey: string, depth = 0): unknown[] | null => {
  if (depth > 8 || value === null || value === undefined || !isRecord(value)) return null

  if (Array.isArray(value[targetKey])) return value[targetKey]

  for (const child of Object.values(value)) {
    const list = findFirstArrayByKey(child, targetKey, depth + 1)
    if (list) return list
  }

  return null
}

const isBrokerSymbol = (value: unknown): value is BrokerSymbol => {
  return isRecord(value) && typeof value.symbol === 'string'
}

const isSymbolCategory = (value: unknown): value is SymbolCategory => {
  return isRecord(value) && typeof value.code === 'string'
}

const isKlineRow = (value: unknown): value is KlineRow => {
  return isRecord(value) && 'timestamp' in value
}

const toPlainText = (value: unknown) => {
  if (typeof value === 'string' || typeof value === 'number') return String(value)
  return ''
}

const readInitConfigSymbols = (payload: unknown) =>
  (findFirstArrayByKey(payload, 'symbols') || []).filter(isBrokerSymbol)

// initconfig 单条约 3.7KB，其中 swap_time 占 74%，而前端与工作台都没有读取这个字段。
// 落盘前剔除后缓存从约 1.5MB 降到约 0.4MB；其余字段保持原样，避免工作台 meta 掉字段。
const OMITTED_CACHED_SYMBOL_FIELDS = ['swap_time']

const toCachedBrokerSymbol = (symbol: BrokerSymbol): BrokerSymbol => {
  const cached = { ...symbol }
  OMITTED_CACHED_SYMBOL_FIELDS.forEach((field) => {
    delete cached[field]
  })
  return cached
}

// 数值解析只接受基础类型，避免对象被 String 后变成 [object Object] 误参与价格计算。
const toNumericText = (value: unknown) => {
  if (typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean') {
    return String(value)
  }

  return ''
}

const parseNumber = (value: unknown) => {
  const parsed = typeof value === 'number' ? value : Number(toNumericText(value).replace(/,/g, ''))
  return Number.isFinite(parsed) ? parsed : Number.NaN
}

const normalizeTimestamp = (timestamp: number) => {
  return timestamp > 0 && timestamp < 1_000_000_000_000 ? timestamp * 1000 : timestamp
}

const dedupeBarsByTime = (bars: KLinePoint[]) => {
  const barMap = new Map<number, KLinePoint>()
  bars.forEach((bar) => {
    barMap.set(bar.time, bar)
  })
  return [...barMap.values()].toSorted((left, right) => left.time - right.time)
}

// 单行 K 线只有 OHLC 都合法才会进入图表，成交量缺失时允许省略。
const parseKlineRow = (row: KlineRow): KLinePoint[] => {
  const rawTime = parseNumber(row.timestamp)
  const time = Number.isFinite(rawTime) ? normalizeTimestamp(rawTime) : rawTime
  const open = parseNumber(row.open_price)
  const high = parseNumber(row.high_price)
  const low = parseNumber(row.low_price)
  const close = parseNumber(row.close_price)
  const volume = parseNumber(
    row.volume ?? row.vol ?? row.v ?? row.tick_volume ?? row.trade_volume ?? row.turnover_volume ?? row.volume_value,
  )

  if (![time, open, high, low, close].every(Number.isFinite)) return []

  return [
    {
      time,
      open,
      high,
      low,
      close,
      ...(Number.isFinite(volume) ? { volume } : {}),
    },
  ]
}

const requestInitConfig = async (baseURL: string, accessToken: string, options: WikiTradeRequestOptions = {}) => {
  const languageCode = normalizeLanguageCode(options.languageCode)
  const payload = await withWikiTradeAuth(
    baseURL,
    accessToken,
    (token) => requestAggregate(baseURL, INIT_CONFIG_ROUTE, { languageCode, web: 'test' }, token, { languageCode }),
    { languageCode },
  )

  // 缓存与网络两条路径返回同一份精简结构，避免页面在后台刷新后拿到形状不同的数据。
  const data = readInitConfigSymbols(payload)
    .filter((item) => item.symbol && item.show_quote !== 2 && item.show_status !== 2)
    .toSorted((left, right) => {
      const leftOrder = Number.isFinite(left.unordered) ? Number(left.unordered) : 9999
      const rightOrder = Number.isFinite(right.unordered) ? Number(right.unordered) : 9999
      return leftOrder - rightOrder || formatSymbolName(left).localeCompare(formatSymbolName(right))
    })
    .map(toCachedBrokerSymbol)

  writeLocalCache(INIT_CONFIG_STORAGE_KEY, data, { ttlMs: INIT_CONFIG_TTL_MS })
  return data
}

// 拉取可交易品种列表。接口实测与语言无关（各语言字段级完全一致），因此全局只保留一份缓存。
// 该数据影响首屏 symbol 解析，所以允许先用本地缓存快速渲染，再后台刷新最新配置，
// 避免用户每次打开页面都等待 initconfig 聚合接口。
export const fetchInitConfig = async (baseURL: string, accessToken: string, options: WikiTradeRequestOptions = {}) => {
  const languageCode = normalizeLanguageCode(options.languageCode)
  removeLegacyInitConfigCaches()

  return loadCachedResource({
    state: getCachedResourceState(initConfigCaches, INIT_CONFIG_STORAGE_KEY),
    storageKey: INIT_CONFIG_STORAGE_KEY,
    ttlMs: INIT_CONFIG_TTL_MS,
    isUsableCache: (value): value is BrokerSymbol[] => Array.isArray(value) && value.length > 0,
    request: () => requestInitConfig(baseURL, accessToken, { languageCode }),
  })
}

/** 仅读取本地 initconfig 缓存，不发起网络请求；供 datafeed 冷启动时快速 resolveSymbol。 */
export const peekCachedInitConfig = () => {
  const cached = readLocalCache(INIT_CONFIG_STORAGE_KEY, { ttlMs: INIT_CONFIG_TTL_MS })
  return Array.isArray(cached) && cached.length > 0 ? (cached as BrokerSymbol[]) : null
}

// 拉取品种分类配置，用于选择弹窗的分类标签。
export const fetchSymbolCategories = async (
  baseURL: string,
  accessToken: string,
  options: WikiTradeRequestOptions = {},
) => {
  const languageCode = normalizeLanguageCode(options.languageCode)
  const payload = await withWikiTradeAuth(
    baseURL,
    accessToken,
    (token) => requestAggregate(baseURL, SYMBOL_CATEGORY_ROUTE, { languageCode }, token, { languageCode }),
    { languageCode },
  )

  return readList(payload)
    .filter(isSymbolCategory)
    .toSorted((left, right) => {
      const leftOrder = Number.isFinite(left.sort) ? Number(left.sort) : Number(left.unordered ?? 9999)
      const rightOrder = Number.isFinite(right.sort) ? Number(right.sort) : Number(right.unordered ?? 9999)
      return leftOrder - rightOrder || getCategoryName(left).localeCompare(getCategoryName(right))
    })
}

const requestCategoryTranslations = async (
  baseURL: string,
  accessToken: string,
  options: WikiTradeRequestOptions = {},
) => {
  const languageCode = normalizeLanguageCode(options.languageCode)
  const payload = await withWikiTradeAuth(
    baseURL,
    accessToken,
    (token) =>
      requestAggregate(baseURL, GLOBAL_TRANSLATE_ROUTE, { account_id: '', languageCode }, token, { languageCode }),
    { languageCode },
  )
  const translations: CategoryTranslationMap = {}

  for (const item of readList(payload)) {
    if (!isRecord(item)) continue

    const code = toPlainText(item.code || item.key)
    const translate = isRecord(item.translate) ? item.translate : {}
    if (!code) continue
    translations[code] =
      toPlainText(translate[languageCode]) ||
      toPlainText(translate['zh-CN']) ||
      toPlainText(translate.zh) ||
      toPlainText(item[languageCode]) ||
      toPlainText(item['zh-CN']) ||
      toPlainText(item.zh_CN) ||
      toPlainText(item.name)
  }

  writeLocalCache(getLanguageCacheKey(TRANSLATIONS_STORAGE_KEY, languageCode), translations, {
    ttlMs: TRANSLATIONS_TTL_MS,
  })
  return translations
}

// 拉取分类翻译文案。翻译接口属于增强数据，使用缓存能减少弹窗打开时的串行等待。
export const fetchCategoryTranslations = async (
  baseURL: string,
  accessToken: string,
  options: WikiTradeRequestOptions = {},
) => {
  const languageCode = normalizeLanguageCode(options.languageCode)
  const storageKey = getLanguageCacheKey(TRANSLATIONS_STORAGE_KEY, languageCode)

  return loadCachedResource({
    state: getCachedResourceState(translationsCaches, storageKey),
    storageKey,
    ttlMs: TRANSLATIONS_TTL_MS,
    isUsableCache: (value): value is CategoryTranslationMap =>
      Boolean(value && typeof value === 'object' && !Array.isArray(value)),
    request: () => requestCategoryTranslations(baseURL, accessToken, { languageCode }),
  })
}

// 拉取历史 K 线并统一去重、排序，供 datafeed 适配层直接消费。
export const fetchKlines = async (
  baseURL: string,
  symbol: string,
  period: Period,
  accessToken: string,
  options: WikiTradeRequestOptions & { limit?: number; beforeTime?: number } = {},
) => {
  const languageCode = normalizeLanguageCode(options.languageCode)
  const rawLimit = options.limit
  const rawBeforeTime = options.beforeTime
  const limit =
    typeof rawLimit === 'number' && Number.isFinite(rawLimit) && rawLimit > 0 ? Math.ceil(rawLimit) : HISTORY_LIMIT
  const endTimestamp = resolveKlineEndTimestamp(rawBeforeTime)
  const payload = await withWikiTradeAuth(
    baseURL,
    accessToken,
    (token) =>
      requestAggregate(
        baseURL,
        KLINES_ROUTE,
        { symbol, period, limit, end_timestamp: endTimestamp, languageCode },
        token,
        {
          languageCode,
        },
      ),
    { languageCode },
  )
  const bars = readList(payload)
    .filter(isKlineRow)
    .flatMap(parseKlineRow)
    .toSorted((left, right) => left.time - right.time)

  return dedupeBarsByTime(bars)
}

/** beforeTime 转为服务端 end_timestamp：取开区间上界，避免重复拉到边界那根 K 线。 */
export const resolveKlineEndTimestamp = (beforeTime?: number) => {
  if (!Number.isFinite(beforeTime)) return 0

  const normalizedBeforeTime = normalizeTimestamp(beforeTime as number)
  const exclusiveBeforeTime = normalizedBeforeTime - 1
  if (!Number.isFinite(exclusiveBeforeTime) || exclusiveBeforeTime <= 0) return 0

  return exclusiveBeforeTime
}

/** 拉取交易商全量品种名称、类型及多语言配置。 */
export const fetchSymbolConfig = async (
  baseURL: string,
  accessToken: string,
  options: WikiTradeRequestOptions = {},
): Promise<WikiTradeSymbolConfig[]> => {
  const languageCode = normalizeLanguageCode(options.languageCode)
  const payload = await withWikiTradeAuth(
    baseURL,
    accessToken,
    (token) => requestAggregate(baseURL, SYMBOL_CONFIG_ROUTE, {}, token, { languageCode }),
    { languageCode },
  )

  return readList(payload).flatMap((item): WikiTradeSymbolConfig[] => {
    if (!isRecord(item)) return []
    const symbol = typeof item.symbol === 'string' ? item.symbol.trim() : ''
    if (!symbol) return []

    const translations =
      item.translations && isRecord(item.translations)
        ? (Object.fromEntries(
            Object.entries(item.translations).filter(([, value]) => typeof value === 'string' && value.trim()),
          ) as Record<string, string>)
        : undefined

    return [
      {
        symbol,
        ...(typeof item.symbol_name === 'string' ? { symbol_name: item.symbol_name } : {}),
        ...(typeof item.symbol_type === 'number' ? { symbol_type: item.symbol_type } : {}),
        ...(translations && Object.keys(translations).length > 0 ? { translations } : {}),
      },
    ]
  })
}

/** 按 locale 解析品种配置里的多语言名称。 */
export const resolveSymbolConfigTranslation = (translations: Record<string, string> | undefined, locale: string) => {
  const normalizedLocale = normalizeLocaleCodeValue(locale)
  const normalizedLanguage = normalizedLocale.split('-')[0]
  const entries = Object.entries(translations || {})
  const findTranslation = (languageCode: string) =>
    entries.find(([key, value]) => normalizeLocaleCodeValue(key) === languageCode && value.trim())?.[1]?.trim()

  return (
    findTranslation(normalizedLocale) ||
    findTranslation(normalizedLanguage || '') ||
    findTranslation('en-us') ||
    findTranslation('en') ||
    entries.find(([key, value]) => normalizeLocaleCodeValue(key).startsWith('en-') && value.trim())?.[1]?.trim()
  )
}

const getBrokerSymbolConfig = (symbol: BrokerSymbol): WikiTradeSymbolConfig | undefined => {
  const config = symbol.symbolConfig
  if (!config || typeof config !== 'object') return undefined
  return config as WikiTradeSymbolConfig
}

/** 品种本地化全称，对应比较商品搜索列表副标题。 */
export const resolveSymbolLegendName = (symbol: BrokerSymbol | undefined, locale: string) => {
  if (!symbol) return ''
  const config = getBrokerSymbolConfig(symbol)
  return (
    resolveSymbolConfigTranslation(config?.translations, locale) ||
    config?.symbol_name?.trim() ||
    formatSymbolName(symbol) ||
    symbol.symbol
  )
}

/** 搜索列表右侧另一商品名；与主标题相同时不重复显示。 */
export const resolveSymbolSecondaryName = (symbol: BrokerSymbol | undefined, locale: string) => {
  if (!symbol) return ''
  const primary = formatSymbolName(symbol)
  const legend = resolveSymbolLegendName(symbol, locale)
  return legend && legend !== primary ? legend : ''
}

/** 商品选择器完整搜索文本，代码与各名称字段都参与匹配。 */
export const getSymbolSearchText = (symbol: BrokerSymbol | undefined, locale: string) => {
  if (!symbol) return ''
  const record = symbol as Record<string, unknown>
  return [
    symbol.symbol,
    record.symbol_name,
    record.name,
    formatSymbolName(symbol),
    resolveSymbolLegendName(symbol, locale),
  ]
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .join(' ')
    .toLowerCase()
}

/** 去掉代码/名称中的分隔符，便于 BTCUSD 匹配 BTC/USD。 */
export const normalizeSymbolSearchKey = (value: string): string => {
  return value.toLowerCase().replace(/[^a-z0-9\u0080-\uffff]+/g, '')
}

/** 原文包含，或去掉分隔符后包含，均视为命中。 */
export const matchesSymbolSearchText = (haystack: string, query: string): boolean => {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) return true
  const lowerHaystack = haystack.toLowerCase()
  if (lowerHaystack.includes(normalizedQuery)) return true
  const compactQuery = normalizeSymbolSearchKey(normalizedQuery)
  if (!compactQuery) return false
  return normalizeSymbolSearchKey(haystack).includes(compactQuery)
}

/** 判断字符是否参与 compact 搜索匹配。 */
const isSearchableChar = (char: string) => /[a-z0-9\u0080-\uffff]/i.test(char)

/** 精确子串高亮拆分，保留原文大小写。 */
const splitExactHighlightedText = (source: string, keyword: string): HighlightedTextPart[] => {
  const lowerSource = source.toLowerCase()
  const lowerKeyword = keyword.toLowerCase()
  const parts: HighlightedTextPart[] = []
  let cursor = 0
  let index = lowerSource.indexOf(lowerKeyword, cursor)

  while (index >= 0) {
    if (index > cursor) parts.push({ text: source.slice(cursor, index), highlight: false })
    parts.push({ text: source.slice(index, index + keyword.length), highlight: true })
    cursor = index + keyword.length
    index = lowerSource.indexOf(lowerKeyword, cursor)
  }

  if (cursor < source.length || !parts.length) parts.push({ text: source.slice(cursor), highlight: false })
  return parts
}

/**
 * 按大小写不敏感规则拆出搜索关键字；查询去掉分隔符后仍可匹配 BTC/USD 这类展示名。
 */
export const splitHighlightedText = (text: string, query: string): HighlightedTextPart[] => {
  const source = text || ''
  const keyword = query.trim()
  if (!source || !keyword) return [{ text: source, highlight: false }]

  if (source.toLowerCase().includes(keyword.toLowerCase())) {
    return splitExactHighlightedText(source, keyword)
  }

  const compactKeyword = normalizeSymbolSearchKey(keyword)
  if (!compactKeyword) return [{ text: source, highlight: false }]

  const parts: HighlightedTextPart[] = []
  let cursor = 0
  let index = 0

  while (index < source.length) {
    let queryIndex = 0
    let end = index
    let matched = false

    while (end < source.length && queryIndex < compactKeyword.length) {
      const char = source[end]
      if (!isSearchableChar(char)) {
        if (!matched) break
        end += 1
        continue
      }
      if (char.toLowerCase() !== compactKeyword[queryIndex]) break
      matched = true
      queryIndex += 1
      end += 1
    }

    if (queryIndex === compactKeyword.length) {
      if (index > cursor) parts.push({ text: source.slice(cursor, index), highlight: false })
      parts.push({ text: source.slice(index, end), highlight: true })
      cursor = end
      index = end
      continue
    }

    index += 1
  }

  if (cursor < source.length || !parts.length) parts.push({ text: source.slice(cursor), highlight: false })
  return parts
}

const formatSymbolName = (symbol?: BrokerSymbol) => {
  if (!symbol) return ''
  const base = symbol.base_currency_name || symbol.base_currency || symbol.symbol
  const settlement = symbol.settlement_currency_name || symbol.settlement_currency
  return settlement ? `${base}/${settlement}` : base
}

const getCategoryName = (category: SymbolCategory, translations?: CategoryTranslationMap | null) => {
  const translated = translations?.[category.code]
  if (translated) return translated
  return category.zh_name || category.name || category.en_name || category.code
}
